import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE, VIEW, EDIT, YES } from "const"
import {
    CcRequirement,
    CcRequirementCreate,
    CcRequirementState,
    CcRequirementReviewMethod,
    CcRequirementSmartCompliance,
    CcRequirementLifecyclePhaseCreate,
    CcRequirementVersionCreate,
} from "server/models"
import {
    StateType,
    useInput,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    RequirementList,
    RequirementValidation,

    GetDefaultType,
    GetDefaultPriority,
    GetDefaultStatus,
    GetDefaultReviewMethods,
    GetDefaultSmartCompliances,
    SaveCcRequirement,
    CreateCcRequirementRevision,
    CreateCcRequirementState,
    CreateCcRequirementVersion,
    GetMaxCcRequirements,
    DeleteCcRequirementLifecyclePhases,
    SaveCcRequirementLifecyclePhases,
    LoadCcRequirementById,
    LoadCcRequirementVersions,

    setCcRequirementEdit,
    setCcRequirementList,
    setCcRequirementVersionList,
    setCcRequirementReloadEdit,

    initCcRequirementState,
    moduleIds,

    addModal,
    closeModal,
    getFile,
    createFileName,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    Feedback,
    SelectDropdown,
    SelectDropdownItem,
    Button,
    SpinnerIcon,
    Empty,
    Delimiter,
} from "client/components"

import Drive from "client/pages/drive"
import LifecyclePhases from "./lifecyclePhases"
import ReviewMethods from "./reviewMethods"
import SmartCompliances from "./smartCompliances"

let isMounted: boolean

interface EditRequirementProps {
    requirement: CcRequirement
}

const Form = ({
    requirement,
}: EditRequirementProps) => {
    const baseRoute = useSelector((state: StoreType) => state.ccRequirement.baseRoute)
    const requirementList = useSelector((state: StoreType) => state.ccRequirement.requirementList)

    const typeList = useSelector((state: StoreType) => state.ccRequirementType.typeList)
    const typeIds = useSelector((state: StoreType) => state.ccRequirementType.typeIds)
    const priorityList = useSelector((state: StoreType) => state.ccRequirementPriority.priorityList)
    const priorityIds = useSelector((state: StoreType) => state.ccRequirementPriority.priorityIds)
    const statusList = useSelector((state: StoreType) => state.ccRequirementStatus.statusList)
    const statusIds = useSelector((state: StoreType) => state.ccRequirementStatus.statusIds)
    const methodList = useSelector((state: StoreType) => state.ccRequirementReviewMethod.methodList)
    const methodIds = useSelector((state: StoreType) => state.ccRequirementReviewMethod.methodIds)
    const complianceList = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceList)
    const complianceIds = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceIds)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const projectModules = JSON.parse(project.modules)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const lifecycleModuleAccess = packageSettings.lifecycleModuleAccess === YES && projectModules[moduleIds.lifecycle]

    const [uniqueId, setUniqueId] = useInput(requirement.uniqueId)
    const [name, setName] = useInput(requirement.name)
    const [description, setDescription] = useInput(requirement.description)

    const [file, setFile] = useState(requirement.file)
    const [typeId, setTypeId] = useState(requirement.typeId)
    const [priorityId, setPriorityId] = useState(requirement.priorityId)
    const [statusId, setStatusId] = useState(requirement.statusId)

    const [reviewMethods, setReviewMethods] = useState(requirement.reviewMethodsParsed || {})
    const [smartCompliances, setSmartCompliances] = useState(requirement.smartComplianceParsed || {})
    const [phases, setPhases] = useState<number[]>([])

    const [validation, setValidation] = useState<RequirementValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (requirement.id) {
            const phaseIds: number[] = []
            requirement.phaseLinks.forEach((row) => {
                phaseIds.push(row.phaseId)
            })

            setPhases(phaseIds)
        }

        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        if (requirement.id) return // only for new requirement
        const defaultType = GetDefaultType(typeList, typeIds)
        setTypeId(defaultType ? defaultType.id : null)
    }, [typeList, typeIds])

    useEffect(() => {
        if (requirement.id) return // only for new requirement
        const defaultPriority = GetDefaultPriority(priorityList, priorityIds)
        setPriorityId(defaultPriority ? defaultPriority.id : null)
    }, [priorityList, priorityIds])

    useEffect(() => {
        if (requirement.id) return // only for new requirement
        const defaultStatus = GetDefaultStatus(statusList, statusIds)
        setStatusId(defaultStatus ? defaultStatus.id : null)
    }, [statusList, statusIds])

    useEffect(() => {
        if (requirement.id) return // only for new requirement
        const defaultMethods = GetDefaultReviewMethods(methodList, methodIds)
        setReviewMethods(defaultMethods)
    }, [methodList, methodIds])

    useEffect(() => {
        if (requirement.id) return // only for new requirement
        const defaultCompliances = GetDefaultSmartCompliances(complianceList, complianceIds)
        setSmartCompliances(defaultCompliances)
    }, [complianceList, complianceIds])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = requirement.id
            ? `${baseRoute}/${VIEW}/${requirement.id}`
            : baseRoute

        forceRerenderContent(path, dispatch)
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        // remove not existing and disabled items
        const reviewMethodsCleared = { ...reviewMethods }
        Object.keys(reviewMethodsCleared).forEach((key) => {
            const keyNumber = Number(key)
            if (!methodList[keyNumber] || !reviewMethodsCleared[keyNumber]) delete reviewMethodsCleared[keyNumber]
        })

        const smartCompliancesCleared = { ...smartCompliances }
        Object.keys(smartCompliancesCleared).forEach((key) => {
            const keyNumber = Number(key)
            if (!complianceList[keyNumber] || !smartCompliancesCleared[keyNumber]) delete smartCompliancesCleared[keyNumber]
        })

        const newRequirementData: CcRequirementCreate = {
            projectId: requirement.projectId || project.id,
            fileId: file ? file.id : null,
            uniqueId,
            name,
            description,
            creator: requirement.creator || userData.userId,
            typeId,
            priorityId,
            statusId,
            reviewMethods: JSON.stringify(reviewMethodsCleared),
            smartCompliance: JSON.stringify(smartCompliancesCleared),
        }

        const { maxRequirements } = GetMaxCcRequirements(packageSettings)
        SaveCcRequirement(
            requirement.id,
            newRequirementData,
            maxRequirements,
            lang,
        ).then((res) => {
            if (res.validation) {
                if (isMounted) {
                    setValidation(res.validation)
                    setSaveState(IDLE)
                    setApplyState(IDLE)
                }
                return
            }

            const promiseArray: Promise<any>[] = []

            // create revision and state for new requirement
            if (res.newRequirementId) {
                promiseArray.push(
                    CreateCcRequirementRevision(res.newRequirementId)
                    .then((resRevision) => {
                        const newRequirementState: CcRequirementState = {
                            ...initCcRequirementState,
                            requirementId: res.newRequirementId,
                            requirementRevisionId: resRevision.id,
                        }

                        return CreateCcRequirementState(newRequirementState).then(() => {
                            if (isMounted) {
                                replaceHistoryState(`${baseRoute}/${EDIT}/${res.newRequirementId}`)
                            }
                        })
                    }),
                )
            }


            // save assigned phases
            if (lifecycleModuleAccess) {
                const phaseData: CcRequirementLifecyclePhaseCreate[] = []
                phases.forEach((phaseId) => {
                    phaseData.push({
                        requirementId: requirement.id || res.newRequirementId,
                        phaseId,
                    })
                })
                promiseArray.push(
                    DeleteCcRequirementLifecyclePhases(requirement.id || res.newRequirementId, null).then(() => (
                        SaveCcRequirementLifecyclePhases(phaseData)
                    )),
                )
            }

            // save requirement version
            let saveVersion = false
            if (res.newRequirementId) saveVersion = true
            else if (
                requirement.uniqueId !== uniqueId
                || requirement.name !== name
                || requirement.description !== description
                || requirement.typeId !== typeId
                || requirement.priorityId !== priorityId
                || requirement.statusId !== statusId
                || JSON.stringify(requirement.reviewMethodsParsed) !== JSON.stringify(reviewMethodsCleared)
                || JSON.stringify(requirement.smartComplianceParsed) !== JSON.stringify(smartCompliancesCleared)
            ) saveVersion = true

            if (saveVersion) {
                const methodsBackup: CcRequirementReviewMethod[] = []
                methodIds.forEach((id) => {
                    methodsBackup.push({
                        ...methodList[id],
                        enabled: reviewMethodsCleared[id] === true,
                    })
                })

                const smartCompliancesBackup: CcRequirementSmartCompliance[] = []
                complianceIds.forEach((id) => {
                    smartCompliancesBackup.push({
                        ...complianceList[id],
                        enabled: smartCompliancesCleared[id] === true,
                    })
                })

                const requirementVersion: CcRequirementVersionCreate = {
                    requirementId: requirement.id || res.newRequirementId,
                    revision: null,
                    uniqueId,
                    name,
                    description,
                    creator: userData.userId,
                    type: JSON.stringify(typeList[typeId]),
                    priority: JSON.stringify(priorityList[priorityId]),
                    status: JSON.stringify(statusList[statusId]),
                    reviewMethods: JSON.stringify(methodsBackup),
                    smartCompliance: JSON.stringify(smartCompliancesBackup),
                }

                promiseArray.push(
                    CreateCcRequirementVersion(requirementVersion),
                )
            }

            Promise.all(promiseArray).then(() => {
                Promise.all([
                    LoadCcRequirementById(requirement.id || res.newRequirementId, project.id)
                    .then((resRequirement) => {
                        const requirementListCopy: RequirementList = {
                            ...requirementList,
                            [resRequirement.id]: resRequirement,
                        }

                        dispatch(setCcRequirementList(requirementListCopy))
                        dispatch(setCcRequirementEdit(resRequirement))
                    }),
                    LoadCcRequirementVersions(requirement.id || res.newRequirementId)
                    .then((resVersions) => {
                        dispatch(setCcRequirementVersionList(
                            requirement.id || res.newRequirementId,
                            resVersions.versionIds,
                            resVersions.versionList,
                        ))
                    }),
                ]).then(() => {
                    if (mode === 1) onClose()
                    if (mode === 2) {
                        dispatch(setCcRequirementReloadEdit(false))
                        setTimeout(() => {
                            dispatch(setCcRequirementReloadEdit(true))
                        }, 0)
                    }
                })
            })
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12} md={4}>
                    <FormGroup>
                        <Label>{lang.uniqueId}</Label>
                        <Input type="text" value={uniqueId} onChange={setUniqueId} />
                        <Feedback>{validation && validation.uniqueId}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12} md={8}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={description} onChange={setDescription} />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.file}</Label>
                        <FormGroup inline>
                            {
                                file && file.versions.length
                                    ? (
                                        <a href={getFile(file, file.versions[0]).download} target="_blank" rel="noreferrer">
                                            {createFileName(file)}
                                        </a>
                                    )
                                    : <Empty />
                            }
                            <Button
                                variant="success"
                                onClick={() => {
                                    dispatch(addModal({
                                        key: "drive",
                                        content: <Drive onSelectFile={(newFile) => setFile(newFile)} />,
                                    }))
                                }}
                            >
                                {file && file.versions.length ? lang.change : lang.select}
                            </Button>
                            {
                                file && file.versions.length && (
                                    <Button onClick={() => setFile(null)}>
                                        {lang.clear}
                                    </Button>
                                )
                            }
                        </FormGroup>
                        <Feedback>{validation && validation.fileId}</Feedback>
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.type}</Label>
                        <SelectDropdown title={typeList[typeId] ? typeList[typeId].name : ""}>
                            {
                                typeIds.map((id) => (
                                    <SelectDropdownItem key={id} onClick={() => setTypeId(id)}>
                                        {typeList[id].name}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.typeId}</Feedback>
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.priority}</Label>
                        <SelectDropdown title={priorityList[priorityId] ? priorityList[priorityId].name : ""}>
                            {
                                priorityIds.map((id) => (
                                    <SelectDropdownItem key={id} onClick={() => setPriorityId(id)}>
                                        {priorityList[id].name}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.priorityId}</Feedback>
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.status}</Label>
                        <SelectDropdown title={statusList[statusId] ? statusList[statusId].name : ""}>
                            {
                                statusIds.map((id) => (
                                    <SelectDropdownItem key={id} onClick={() => setStatusId(id)}>
                                        {statusList[id].name}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.statusId}</Feedback>
                    </FormGroup>

                    <Delimiter />

                    <FormGroup>
                        <Label>{lang.reviewMethods}</Label>
                        <ReviewMethods
                            reviewMethods={reviewMethods}
                            setReviewMethods={(newReviewMethods) => setReviewMethods(newReviewMethods)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.smartCompliance}</Label>
                        <SmartCompliances
                            smartCompliances={smartCompliances}
                            setSmartCompliances={(newSmartCompliances) => setSmartCompliances(newSmartCompliances)}
                        />
                    </FormGroup>

                    {
                        lifecycleModuleAccess
                        && <Delimiter />
                    }
                    {
                        lifecycleModuleAccess && (
                            <LifecyclePhases
                                phases={phases}
                                setPhases={(newPhases) => setPhases(newPhases)}
                            />
                        )
                    }
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
